import React, { useState } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useDispatch } from "react-redux";
import { addSystemAnnouncementApi } from "store/actions";
import { Validation } from "views/systemAnnouncement/Validation";
import LoadingButton from "components/shared/LoadingButton";
import { DATE_FORMAT } from "helpers/common";

export default function AddSystemAnnouncementModal({
  show,
  handleClose,
  loading,
}) {
  const dispatch = useDispatch();
  const [scheduledAtInput, setScheduledAtInput] = useState("");

  const initialValues = {
    message: "",
    type: "",
    description: "",
    isMaintenance: null,
    actualStartAt: "",
    actualEndAt: "",
    estimatedDuration: 0,
    scheduledTime: [],
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const updateMaintenanceFields = (values, setFieldValue) => {
    if (values.type !== "Info" || !values.isMaintenance) return;

    let description = values.message || "";

    if (values.actualStartAt) {
      description = `${values.message} ${formatDate(values.actualStartAt)}`;
    }

    if (values.actualStartAt && values.actualEndAt) {
      const start = new Date(values.actualStartAt);
      const end = new Date(values.actualEndAt);

      const duration = Math.round(
        (end.getTime() - start.getTime()) / 60000
      );

      setFieldValue("estimatedDuration", duration);

      const sameDay =
        start.toDateString() === end.toDateString();

      if (sameDay) {
        description = `${values.message} ${formatDate(start)} - ${formatTime(end)}`;
      } else {
        description = `${values.message} ${formatDate(start)} - ${formatDate(end)}`;
      }
    }

    setFieldValue("description", description);
  };

  const handleSubmit = (values) => {
    const isInfoMaintenance = values.type === "Info" && values.isMaintenance === true;

    const payload = {
      ...values,
      message: isInfoMaintenance ? values.message : null,
      actualStartAt: isInfoMaintenance ? new Date(values.actualStartAt).toISOString() : null,
      actualEndAt: isInfoMaintenance ? new Date(values.actualEndAt).toISOString() : null,
      estimatedDuration: isInfoMaintenance ? Number(values.estimatedDuration) : null,
      scheduledTime: isInfoMaintenance ? values.scheduledTime : [],
    };

    dispatch(
      addSystemAnnouncementApi({
        data: payload,
        callback: (err) => {
          if (!err) handleClose();
        },
      }),
    );
  };

  const now = new Date();
  const currentDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header className="py-3 text-dark" closeButton>
        <Modal.Title className="fw-bold text-dark">Add System Announcement</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={Validation}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form className="container">
              <Row>
                {/* Type */}
                <Col md={6} className="mb-3">
                  <label className="form-label">
                    Type <span className="text-danger">*</span>
                  </label>

                  <Field
                    as="select"
                    name="type"
                    className={`form-control ${errors.type && touched.type ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      const value = e.target.value;

                      setFieldValue("type", value);

                      // Reset dependent fields
                      setFieldValue("isMaintenance", null);
                      setFieldValue("message", "");
                      setFieldValue("description", "");
                      setFieldValue("actualStartAt", "");
                      setFieldValue("actualEndAt", "");
                      setFieldValue("estimatedDuration", 0);
                      setFieldValue("scheduledTime", []);
                    }}
                  >
                    <option value="">Select Type</option>
                    <option value="Info">Info</option>
                    <option value="Warning">Warning</option>
                    <option value="Critical">Critical</option>
                  </Field>

                  <ErrorMessage
                    name="type"
                    component="div"
                    className="invalid-feedback"
                  />
                </Col>

              </Row>

              {values.type === "Info" && (
                <Row className="mb-3">
                  <Col md={4}>
                    <label className="form-label fw-bold">
                      Maintenance Announcement
                    </label>
                  </Col>

                  <Col md={8}>
                    <div className="d-flex gap-4">

                      <label className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          checked={values.isMaintenance === true}
                          onChange={() => setFieldValue("isMaintenance", true)}
                        />
                        <span className="ms-2">Yes</span>
                      </label>

                      <label className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          checked={values.isMaintenance === false}
                          onChange={() => setFieldValue("isMaintenance", false)}
                        />
                        <span className="ms-2">No</span>
                      </label>

                    </div>
                  </Col>
                </Row>
              )}


              {values.type === "Info" && values.isMaintenance && (
                <Col md={12} className="mb-3">
                  <label className="form-label">
                    Message <span className="text-danger">*</span>
                  </label>

                  <Field
                    name="message"
                    type="text"
                    placeholder="Enter maintenance message..."
                    className={`form-control ${errors.message && touched.message ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      const value = e.target.value;

                      setFieldValue("message", value);

                      updateMaintenanceFields(
                        {
                          ...values,
                          message: value,
                        },
                        setFieldValue
                      );
                    }}
                  />

                  <ErrorMessage
                    name="message"
                    component="div"
                    className="invalid-feedback"
                  />
                </Col>
              )}


              {/* {(values.type === "Warning" || values.type === "Critical" || (values.type === "Info" && values.isMaintenance === false) || (values.type === "Info" && values.isMaintenance === true)) && ( */}
              {values.type === "Info" && values.isMaintenance && (
                <Row>

                  <Col md={6}>
                    <label className="form-label">
                      Actual Start At
                      {values.type === "Info" && values.isMaintenance && (
                        <span className="text-danger">*</span>
                      )}
                    </label>

                    <Field
                      type="datetime-local"
                      name="actualStartAt"
                      className={`form-control ${errors.actualStartAt && touched.actualStartAt
                        ? "is-invalid"
                        : ""
                        }`}
                      onChange={(e) => {
                        const value = e.target.value;

                        setFieldValue("actualStartAt", value);

                        updateMaintenanceFields(
                          {
                            ...values,
                            actualStartAt: value,
                          },
                          setFieldValue
                        );
                      }}
                    />

                    <ErrorMessage
                      name="actualStartAt"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label">
                      Actual End At
                    </label>

                    <Field
                      type="datetime-local"
                      name="actualEndAt"
                      className={`form-control ${errors.actualEndAt && touched.actualEndAt
                        ? "is-invalid"
                        : ""
                        }`}
                      onChange={(e) => {
                        const value = e.target.value;

                        setFieldValue("actualEndAt", value);

                        updateMaintenanceFields(
                          {
                            ...values,
                            actualEndAt: value,
                          },
                          setFieldValue
                        );
                      }}
                    />

                    <ErrorMessage
                      name="actualEndAt"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Col>

                </Row>
              )}

              {(values.type === "Warning" || values.type === "Critical" || (values.type === "Info" && values.isMaintenance === false) || (values.type === "Info" && values.isMaintenance === true)) && (
                <Row>

                  <Col md={12} className="mb-3">
                    <label className="form-label">
                      Description
                      <span className="text-danger">*</span>
                    </label>

                    <Field
                      as="textarea"
                      rows={3}
                      name="description"
                      disabled={values.type === "Info" && values.isMaintenance}
                      placeholder={
                        values.type === "Info" && values.isMaintenance
                          ? "Description will be generated automatically"
                          : "Enter Description..."
                      }
                      className={`form-control ${errors.description && touched.description ? "is-invalid" : ""
                        }`}
                    />

                    <ErrorMessage
                      name="description"
                      component="div"
                      className="invalid-feedback"
                    />
                  </Col>

                </Row>
              )}

              {values.type === "Info" && values.isMaintenance && (
                <Row className="mb-3">
                  <Col md={6}>
                    <label className="form-label">
                      Estimated Duration (Minutes)
                    </label>

                    <Field
                      name="estimatedDuration"
                      type="number"
                      className="form-control"
                      disabled
                    />
                  </Col>
                </Row>
              )}

              {values.type === "Info" && values.isMaintenance && (
                <>
                  <Row className="mb-3">
                    <Col md={8}>
                      <label className="form-label">
                        Schedule Time <span className="text-danger">*</span>
                      </label>

                      <input
                        type="datetime-local"
                        className="form-control"
                        value={scheduledAtInput}
                        min={currentDateTime}
                        max={values.actualStartAt}
                        disabled={!values.actualStartAt}
                        onChange={(e) => setScheduledAtInput(e.target.value)}
                      />
                    </Col>

                    <Col md={4} className="d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        disabled={
                          !scheduledAtInput ||
                          !values.actualStartAt ||
                          !values.actualEndAt
                        }
                        onClick={() => {
                          const selected = new Date(scheduledAtInput);
                          const now = new Date();
                          const start = new Date(values.actualStartAt);

                          if (selected < now || selected > start) {
                            alert(
                              "Please select a schedule time between the current time and Actual Start At."
                            );
                            return;
                          }

                          const utcTime = selected.toISOString();

                          const exists = values.scheduledTime.some(
                            (item) => item.scheduledAt === utcTime
                          );

                          if (exists) {
                            alert("This schedule time already exists.");
                            return;
                          }

                          setFieldValue("scheduledTime", [
                            ...values.scheduledTime,
                            { scheduledAt: utcTime },
                          ]);

                          setScheduledAtInput("");
                        }}
                      >
                        Add
                      </button>
                    </Col>
                  </Row>

                  {values.scheduledTime.length > 0 && (
                    <Row>
                      <Col md={12}>
                        <label className="form-label fw-bold">
                          Selected Schedule Times
                        </label>

                        <div
                          className="border rounded p-3 bg-light"
                          style={{
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {values.scheduledTime.map((item, index) => (
                            <div
                              key={index}
                              className="d-flex justify-content-between align-items-center mb-2 p-2 bg-white rounded border"
                            >
                              <span>{DATE_FORMAT(item.scheduledAt)}</span>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  setFieldValue(
                                    "scheduledTime",
                                    values.scheduledTime.filter(
                                      (_, i) => i !== index
                                    )
                                  )
                                }
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              )}

              <Row className="justify-content-end mt-4">
                <Col md={3}>
                  <LoadingButton
                    type="submit"
                    className="btn btn-primary btn-lg w-100"
                    loading={loading}
                  >
                    Submit
                  </LoadingButton>
                </Col>
              </Row>

            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}
